import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  addCandidateToProject,
  addCandidateToProjectError,
  addCandidateToProjectSuccess,
  deleteCandidateFromTheProject,
  deleteCandidateFromTheProjectError,
  deleteProject,
  deleteProjectError,
  deleteProjectSuccess,
  loadBarChartData,
  loadBarChartDataFailure,
  loadBarChartDataSuccess,
  loadCandidatesForSelectedProject,
  loadProjects,
  loadProjectsError,
  loadProjectsSuccess,
  projectChangeExternalCandidateStatusFastr,
  projectChangeExternalCandidateStatusFastrFailure,
  projectChangeExternalCandidateStatusFastrSuccess,
  resetBarChartData,
  saveProjectAndAssignCandidate,
  saveProjectAndAssignCandidateError,
  saveProjectAndAssignCandidateSuccess,
  updateProject,
  updateProjectNameFail,
  updateProjectSuccess,
} from './projects.actions';
import { filter, map, of, switchMap, take, tap } from 'rxjs';
import { catchError, withLatestFrom } from 'rxjs/operators';
import { ProjectsService } from '../../shared/services/projects.service';
import { selectCandidateSummary } from '../candidates/candidates.selectors';
import { Store } from '@ngrx/store';
import {
  getIsLikedTabActive,
  getSelectedProject,
  getSelectedProjectId,
} from './projects.selectors';
import { deleteCandidate, loadCandidates } from '../candidates/candidates.actions';
import { ProjectPool } from '../../shared/models/project-pool';
import { Router } from '@angular/router';
import { NLS_SEARCH_URL } from '../../app-routing-paths';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TalentPoolsService } from '@app/shared/services/talent-pools.service';
import { SidenavNavigationService } from '@app/modules/authenticated-view/sidenav-navigation/services/sidenav-navigation.service';
import { talentPoolWithDetailsLoad } from 'mojo-talent-pools';

@Injectable()
export class ProjectsEffects {
  actions$ = inject(Actions);
  projectsService = inject(ProjectsService);
  store = inject(Store);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  talentPoolsService = inject(TalentPoolsService);
  protected sidenavNavigationService = inject(SidenavNavigationService);

  loadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProjects),
      switchMap(() =>
        this.projectsService.getProjectsAll().pipe(
          map((projects) => loadProjectsSuccess({ payload: { projects } })),
          catchError((error) => of(loadProjectsError({ error }))),
        ),
      ),
    ),
  );

  editProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProject),
      switchMap(({ payload }) =>
        this.projectsService.updateProject(payload.project).pipe(
          switchMap((updatedProject) => {
            this.sidenavNavigationService.emitTalentPoolUpdate(updatedProject.talentPool);
            const updateSuccessAction = updateProjectSuccess({
              payload: { project: updatedProject },
            });

            return [updateSuccessAction];
          }),
          catchError((error) => of(updateProjectNameFail({ error }))),
        ),
      ),
    ),
  );

  addCandidateToProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCandidateToProject),
      concatLatestFrom(({ payload }) => [
        this.store.select(getSelectedProjectId),
        this.store.select(selectCandidateSummary(payload.candidateId)),
      ]),
      switchMap(([{ payload }, projectId, candidateSummary]) =>
        this.projectsService
          .addCandidateToProject(payload.projectId, payload.candidateId, candidateSummary)
          .pipe(
            map(() => {
              const snackbarText = projectId
                ? 'Candidate added to Liked.'
                : 'Candidate added to the project.';

              this.snackBar.open(snackbarText, '', {
                duration: 3000,
              });

              return addCandidateToProjectSuccess({
                payload: {
                  projectId,
                  candidateId: payload.candidateId,
                },
              });
            }),
            catchError((error) => of(addCandidateToProjectError({ error }))),
          ),
      ),
    ),
  );

  deleteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProject),
      switchMap(({ payload }) =>
        this.projectsService.deleteProject(payload.projectId).pipe(
          map(() => deleteProjectSuccess({ payload: { projectId: payload.projectId } })),
          catchError((error) => of(deleteProjectError({ error }))),
        ),
      ),
    ),
  );

  deleteProjectSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteProjectSuccess),
        withLatestFrom(this.store.select(getSelectedProjectId)),
        tap(([{ payload }, projectId]) => {
          if (payload.projectId === projectId) {
            this.router.navigate([NLS_SEARCH_URL]);
          }
        }),
      ),
    { dispatch: false },
  );

  saveProjectAndAssignCandidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveProjectAndAssignCandidate),
      concatLatestFrom(({ payload }) => [
        this.store.select(selectCandidateSummary(payload.candidateId)),
      ]),
      switchMap(([action, candidateSummary]) => {
        const candidateToAdd = {
          externalSourcingId: action.payload.candidateId,
          summary: candidateSummary,
        };

        return this.projectsService.createProject([candidateToAdd], action.payload.talentPool).pipe(
          map((project) => {
            this.snackBar.open('Candidate added to the new project.', '', {
              duration: 3000,
            });

            return saveProjectAndAssignCandidateSuccess({
              payload: { project },
            });
          }),
          catchError((error) => of(saveProjectAndAssignCandidateError(error))),
        );
      }),
    ),
  );

  loadCandidatesForSelectedProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCandidatesForSelectedProject),
      withLatestFrom(this.store.select(getIsLikedTabActive)),
      switchMap(([, isLikedTabActive]) =>
        this.store.select(getSelectedProject).pipe(
          filter((project) => !!project),
          take(1),
          map((project: ProjectPool | null) => {
            if (!isLikedTabActive) {
              this.store.dispatch(resetBarChartData());
            } else {
              this.store.dispatch(
                loadBarChartData({ talentPoolId: Number(project?.talentPool.id) }),
              );
              this.store.dispatch(
                talentPoolWithDetailsLoad({ id: Number(project?.talentPool.id) }),
              );
            }

            return loadCandidates({
              payload: { page: 0 },
            });
          }),
        ),
      ),
    ),
  );

  addCandidateToProjectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCandidateToProjectSuccess),
      withLatestFrom(this.store.select(getSelectedProjectId)),
      filter(([, selectedProjectId]) => !!selectedProjectId),
      map(([{ payload }]) =>
        deleteCandidate({
          payload: { candidateId: payload.candidateId },
        }),
      ),
    ),
  );

  deleteCandidateFromTheProjectError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCandidateFromTheProject),
      switchMap(({ payload }) => {
        return this.projectsService
          .deleteCandidatesFromTheProject(payload.projectId, payload.candidateIds)
          .pipe(
            switchMap(() => {
              this.snackBar.open('Candidate removed from the project.', '', {
                duration: 3000,
              });
              return [
                deleteCandidate({
                  payload: { candidateId: payload.candidateIds[0] },
                }),
                loadBarChartData({ talentPoolId: Number(payload.projectId) }),
              ];
            }),
            catchError((error) => of(deleteCandidateFromTheProjectError({ error }))),
          );
      }),
    ),
  );

  talentPoolChangeExternalCandidateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(projectChangeExternalCandidateStatusFastr),
      switchMap(({ candidateId, status, talentPoolId }) =>
        this.projectsService
          .changeProjectExternalCandidateStatus(talentPoolId!, candidateId, { status })
          .pipe(
            switchMap(() => [
              projectChangeExternalCandidateStatusFastrSuccess(),
              loadBarChartData({ talentPoolId: talentPoolId! }),
            ]),
            catchError((error) => of(projectChangeExternalCandidateStatusFastrFailure({ error }))),
          ),
      ),
    ),
  );

  loadBarChartData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBarChartData),
      switchMap(({ talentPoolId }) =>
        this.talentPoolsService.getBarChartData(talentPoolId).pipe(
          map((data) => loadBarChartDataSuccess({ data })),
          catchError((error) => of(loadBarChartDataFailure({ error }))),
        ),
      ),
    ),
  );
}
